import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FooterWC26 from '../../components/FooterWC26/FooterWC26';
import HeaderWC26 from '../../components/HeaderWC26/HeaderWC26';
import { MatchTooltip, Tooltip } from '../../components/ScoreboardWC26/ScoreTooltip';
import Spinner from '../../components/Spinner/Spinner';
import {
  useAllMatchStatsWC26,
  useBracketStandings,
  useDirectFkGoals,
  useGoalSeconds,
  useGroupWinnersWC26,
  useKeeperGoals,
  useOlimpicoGoals,
  useTopScorersWC26,
  useWC26ScoringRules,
  useWC26UserTeams,
} from '../../queries';
import {
  Goal, MatchStats, Scores, TeamResults, UserTeams,
} from '../../types';
import {
  calculateResults, calculateScores, getMostRedsWinnerIds, goalLatestSortKey, goalTotalSeconds,
} from '../../utils/scoringUtils';
import './WC26UserPage.css';

function totUpScore(s: Scores): number {
  return s.score + s.groupWinner + s.goldenBoot + s.earliestGoal
    + s.latestGoal + s.hattrick + s.directFk + s.olimpico
    + s.keeperGoal + s.mostReds + s.teamGoals3plus;
}

interface BreakdownRowProps {
  label: string;
  value: number;
  tooltip?: React.ReactNode;
}

function BreakdownRow({ label, value, tooltip }: BreakdownRowProps) {
  const cls = value > 0 ? 'pos' : value < 0 ? 'neg' : 'zero';
  const valueEl = (
    <span className={`wc26-user-breakdown-value ${cls}`}>
      {value > 0 ? `+${value}` : value}
    </span>
  );
  return (
    <div className="wc26-user-breakdown-row">
      <span className="wc26-user-breakdown-label">{label}</span>
      {tooltip ? (
        <Tooltip content={tooltip} direction="up">{valueEl}</Tooltip>
      ) : valueEl}
    </div>
  );
}

interface MatchRowProps {
  match: MatchStats;
  userTeam: UserTeams;
  rules: Record<string, number>;
  directFkKeys: string[];
  olimpicoKeys: string[];
  keeperKeys: string[];
  mostRedsWinnerIds: Set<string>;
}

function MatchRow({
  match, userTeam, rules, directFkKeys, olimpicoKeys, keeperKeys, mostRedsWinnerIds,
}: MatchRowProps) {
  const home = match.statistics[0];
  const away = match.statistics[1];
  if (!home || !away) return null;

  const isT1Home = home.id.toString() === userTeam.team1Id;
  const isT1Away = away.id.toString() === userTeam.team1Id;
  const isT2Home = home.id.toString() === userTeam.team2Id;
  const isT2Away = away.id.toString() === userTeam.team2Id;

  if (!isT1Home && !isT1Away && !isT2Home && !isT2Away) return null;

  const teamResults = calculateResults([userTeam], [match]);
  const [score] = calculateScores(
    teamResults,
    [userTeam],
    rules as any,
    undefined,
    undefined,
    undefined,
    undefined,
    [match],
    directFkKeys,
    olimpicoKeys,
    keeperKeys,
    mostRedsWinnerIds
  );

  const pts = totUpScore(score);
  const ptsClass = pts > 0 ? 'pos' : pts < 0 ? 'neg' : 'zero';

  const userTeamIsHome = isT1Home || isT2Home;
  const userSide = userTeamIsHome ? home : away;
  const oppSide = userTeamIsHome ? away : home;

  return (
    <div className="wc26-user-match">
      <div className="wc26-user-match-teams">
        <img src={userSide.logo} alt={userSide.name} />
        <span className="wc26-user-match-team-name">{userSide.name}</span>
      </div>
      <div className="wc26-user-match-score">
        {userTeamIsHome
          ? `${match.score.home} – ${match.score.away}`
          : `${match.score.away} – ${match.score.home}`}
      </div>
      <div className="wc26-user-match-teams" style={{ justifyContent: 'flex-end' }}>
        <span className="wc26-user-match-team-name">{oppSide.name}</span>
        <img src={oppSide.logo} alt={oppSide.name} />
      </div>
      <div className={`wc26-user-match-pts ${ptsClass}`}>
        <MatchTooltip score={score} direction="up" label={pts > 0 ? `+${pts}` : `${pts}`} />
      </div>
    </div>
  );
}

function WC26UserPage() {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();

  const [userTeamsLoading, allUserTeams] = useWC26UserTeams();
  const [matchStatsLoading, matchStats] = useAllMatchStatsWC26();
  const [rulesLoading, rules] = useWC26ScoringRules();
  const [topScorersLoading, topScorers] = useTopScorersWC26();
  const [groupWinnersLoading, groupWinners] = useGroupWinnersWC26();
  const [, standings] = useBracketStandings();
  const [, directFkGoals] = useDirectFkGoals();
  const [, olimpicoGoals] = useOlimpicoGoals();
  const [, keeperGoals] = useKeeperGoals();
  const [, goalSecondsData] = useGoalSeconds();
  const secondsMap = goalSecondsData ?? {};

  useEffect(() => {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) favicon.href = '/favicon_wc.ico';
  }, []);

  const loading = userTeamsLoading || matchStatsLoading || rulesLoading
    || topScorersLoading || groupWinnersLoading;

  const decodedName = decodeURIComponent(name ?? '');
  const userTeam = allUserTeams?.find((u) => u.name === decodedName);

  const findGroup = (teamId: string) => standings?.find(
    (s) => s.team.id.toString() === teamId
  )?.group ?? '';

  const allGoalsWithMatch = (matchStats ?? []).reduce<{ goal: Goal; matchId: number }[]>(
    (acc, m) => [...acc, ...m.goalEvents
      .filter((g) => g.type !== 'Own Goal' && g.type !== 'Missed Penalty')
      .map((g) => ({ goal: g, matchId: m.id }))],
    []
  );
  const earliestGoal = [...allGoalsWithMatch].sort(
    (a, b) => goalTotalSeconds(a.matchId, a.goal, secondsMap)
      - goalTotalSeconds(b.matchId, b.goal, secondsMap)
  )[0]?.goal;
  const latestGoal = [...allGoalsWithMatch].sort(
    (a, b) => goalLatestSortKey(b.goal) - goalLatestSortKey(a.goal)
  )[0]?.goal;

  const allTeamResults = (!loading && allUserTeams && matchStats)
    ? calculateResults(allUserTeams, matchStats)
    : null;

  const mostRedsWinnerIds = (allTeamResults && allUserTeams)
    ? getMostRedsWinnerIds(allTeamResults, allUserTeams)
    : new Set<string>();

  const teamResults = (!loading && userTeam && matchStats)
    ? calculateResults([userTeam], matchStats)
    : null;

  const teamMatchScore = (teamId: string, isT1: boolean): number => {
    if (!teamResults || !rules) return 0;
    const r = teamResults[teamId];
    if (!r) return 0;
    const winRule = isT1 ? rules.t1Win : rules.t2Win;
    const drawRule = isT1 ? rules.t1Draw : rules.t2Draw;
    return r.wins * winRule + r.draws * drawRule
      + r.cleanSheets * rules.cleanSheet + r.redCards * rules.redCard
      + r.ownGoals * rules.ownGoal;
  };

  const score: Scores | null = (!loading && userTeam && rules && matchStats)
    ? calculateScores(
      calculateResults([userTeam], matchStats ?? []),
      [userTeam],
      rules as any,
      topScorers?.[0]?.teamId,
      (groupWinners ?? []).filter((w) => w.group !== 'Group Stage'),
      earliestGoal,
      latestGoal,
      matchStats ?? [],
      directFkGoals ?? [],
      olimpicoGoals ?? [],
      keeperGoals ?? [],
      mostRedsWinnerIds
    )[0]
    : null;

  const teamCardTooltip = (teamId: string, teamName: string, isT1: boolean): React.ReactNode => {
    if (!teamResults || !rules) return null;
    const r: TeamResults | undefined = teamResults[teamId];
    if (!r) return null;
    const winRule = isT1 ? rules.t1Win : rules.t2Win;
    const drawRule = isT1 ? rules.t1Draw : rules.t2Draw;
    const rows: { label: string; value: number }[] = [
      { label: `${r.wins} win${r.wins !== 1 ? 's' : ''}`, value: r.wins * winRule },
      { label: `${r.draws} draw${r.draws !== 1 ? 's' : ''}`, value: r.draws * drawRule },
      { label: `${r.cleanSheets} clean sheet${r.cleanSheets !== 1 ? 's' : ''}`, value: r.cleanSheets * rules.cleanSheet },
      { label: `${r.redCards} red card${r.redCards !== 1 ? 's' : ''}`, value: r.redCards * rules.redCard },
      { label: `${r.ownGoals} own goal${r.ownGoals !== 1 ? 's' : ''}`, value: r.ownGoals * rules.ownGoal }
    ].filter((row) => row.value !== 0);
    return (
      <div>
        <div className="score-tooltip-title">{teamName}</div>
        {rows.length === 0 && <div className="score-tooltip-note">No points yet</div>}
        {rows.map((row) => (
          <div key={row.label} className="score-tooltip-row">
            <span className="score-tooltip-label">{row.label}</span>
            <span className={`score-tooltip-value ${row.value > 0 ? 'pos' : 'neg'}`}>
              {row.value > 0 ? `+${row.value}` : row.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const matchResultsTooltip = score ? (
    <div>
      <div className="score-tooltip-title">Match Results</div>
      {[
        { label: 'Wins', value: score.winsScore },
        { label: 'Draws', value: score.drawsScore },
        { label: 'Clean Sheets', value: score.cleanSheetsScore },
        { label: 'Red Cards', value: score.redCardsScore },
        { label: 'Own Goals', value: score.ownGoalsScore }
      ].filter((r) => r.value !== 0).map((r) => (
        <div key={r.label} className="score-tooltip-row">
          <span className="score-tooltip-label">{r.label}</span>
          <span className={`score-tooltip-value ${r.value > 0 ? 'pos' : 'neg'}`}>
            {r.value > 0 ? `+${r.value}` : r.value}
          </span>
        </div>
      ))}
    </div>
  ) : null;

  const filteredGroupWinners = (groupWinners ?? []).filter((w) => w.group !== 'Group Stage');
  const groupWinnersTooltip = (score && userTeam) ? (() => {
    const teams = [
      { name: userTeam.team1, id: userTeam.team1Id, rule: rules?.t1WinGroup ?? 0 },
      { name: userTeam.team2, id: userTeam.team2Id, rule: rules?.t2WinGroup ?? 0 }
    ];
    const rows = teams.map((t) => {
      const gw = filteredGroupWinners.find((w) => w.teamId.toString() === t.id);
      return { name: t.name, group: gw?.group ?? null, pts: t.rule };
    });
    return (
      <div>
        <div className="score-tooltip-title">Group Winners</div>
        {rows.map((r) => (
          <div key={r.name} className="score-tooltip-row">
            <span className="score-tooltip-label">{r.name}</span>
            <span className={`score-tooltip-value ${r.group ? 'pos' : ''}`}>
              {r.group ? `Won ${r.group} (+${r.pts})` : '—'}
            </span>
          </div>
        ))}
      </div>
    );
  })() : null;

  const goldenBootTooltip = (score && userTeam && topScorers?.[0]) ? (() => {
    const topTeamId = topScorers[0].teamId?.toString();
    const t1HasIt = userTeam.team1Id === topTeamId;
    const t2HasIt = userTeam.team2Id === topTeamId;
    const winner = t1HasIt ? userTeam.team1 : t2HasIt ? userTeam.team2 : null;
    return (
      <div>
        <div className="score-tooltip-title">Golden Boot</div>
        <div className="score-tooltip-note">
          {winner ? `${winner} has the top scorer` : 'Not awarded to your teams'}
        </div>
      </div>
    );
  })() : null;

  const fastestGoalTooltip = (score && userTeam && earliestGoal) ? (() => {
    const teamId = earliestGoal.team.toString();
    const teamName = teamId === userTeam.team1Id ? userTeam.team1
      : teamId === userTeam.team2Id ? userTeam.team2 : null;
    return (
      <div>
        <div className="score-tooltip-title">Fastest Goal</div>
        <div className="score-tooltip-note">
          {teamName
            ? `${teamName} scored the fastest goal (${earliestGoal.time}')`
            : 'Not scored by your teams'}
        </div>
      </div>
    );
  })() : null;

  const latestGoalTooltip = (score && userTeam && latestGoal) ? (() => {
    const teamId = latestGoal.team.toString();
    const teamName = teamId === userTeam.team1Id ? userTeam.team1
      : teamId === userTeam.team2Id ? userTeam.team2 : null;
    const timeStr = latestGoal.extra
      ? `${latestGoal.time}+${latestGoal.extra}'` : `${latestGoal.time}'`;
    return (
      <div>
        <div className="score-tooltip-title">Latest Goal</div>
        <div className="score-tooltip-note">
          {teamName
            ? `${teamName} scored the latest goal (${timeStr})`
            : 'Not scored by your teams'}
        </div>
      </div>
    );
  })() : null;

  const bonusCountTooltip = (title: string, pts: number, ruleVal: number):
   React.ReactNode | null => {
    if (!score) return null;
    const count = ruleVal > 0 ? Math.round(pts / ruleVal) : 0;
    return (
      <div>
        <div className="score-tooltip-title">{title}</div>
        <div className="score-tooltip-note">
          {count > 0 ? `${count} × +${ruleVal} pts each` : 'None yet'}
        </div>
      </div>
    );
  };

  const mostRedsTooltip = (score && userTeam && allTeamResults) ? (() => {
    const teams = [
      { name: userTeam.team1, id: userTeam.team1Id },
      { name: userTeam.team2, id: userTeam.team2Id }
    ];
    const winner = teams.find((t) => mostRedsWinnerIds.has(t.id));
    const t1Reds = allTeamResults[userTeam.team1Id]?.redCards ?? 0;
    const t2Reds = allTeamResults[userTeam.team2Id]?.redCards ?? 0;
    return (
      <div>
        <div className="score-tooltip-title">Most Reds</div>
        {teams.map((t) => {
          const reds = t.id === userTeam.team1Id ? t1Reds : t2Reds;
          return (
            <div key={t.id} className="score-tooltip-row">
              <span className="score-tooltip-label">{t.name}</span>
              <span className="score-tooltip-value">{`${reds} red card${reds !== 1 ? 's' : ''}`}</span>
            </div>
          );
        })}
        <div className="score-tooltip-note">
          {winner ? `${winner.name} has the most reds — bonus awarded!` : 'Bonus not yet awarded'}
        </div>
      </div>
    );
  })() : null;

  const goals3plusTooltip = (score && rules) ? (() => {
    const ruleVal = rules['3+goals'] ?? 0;
    const count = ruleVal > 0 ? Math.round(score.teamGoals3plus / ruleVal) : 0;
    const t1Count = teamResults?.[userTeam!.team1Id]?.teamGoals3plus ?? 0;
    const t2Count = teamResults?.[userTeam!.team2Id]?.teamGoals3plus ?? 0;
    return (
      <div>
        <div className="score-tooltip-title">Team 3+ Goals</div>
        <div className="score-tooltip-row">
          <span className="score-tooltip-label">{userTeam!.team1}</span>
          <span className="score-tooltip-value">{`${t1Count} game${t1Count !== 1 ? 's' : ''}`}</span>
        </div>
        <div className="score-tooltip-row">
          <span className="score-tooltip-label">{userTeam!.team2}</span>
          <span className="score-tooltip-value">{`${t2Count} game${t2Count !== 1 ? 's' : ''}`}</span>
        </div>
        <div className="score-tooltip-note">
          {count > 0 ? `${count} total × +${ruleVal} pts each` : 'None yet'}
        </div>
      </div>
    );
  })() : null;

  return (
    <div className="wc26-user-page">
      <HeaderWC26 />
      <div className="wc26-user-main">
        {loading && <Spinner />}

        {!loading && !userTeam && (
          <p style={{ color: '#fff' }}>
            {`User "${decodedName}" not found.`}
          </p>
        )}

        {!loading && userTeam && score && (
          <>
            <div className="wc26-user-header">
              <h1 className="wc26-user-name">{userTeam.name}</h1>
              <div className="wc26-user-total">{`${totUpScore(score) >= 0 ? '+' : ''}${totUpScore(score)} pts`}</div>
            </div>

            <div className="wc26-user-teams">
              {[
                { name: userTeam.team1, id: userTeam.team1Id, label: 'Team 1' },
                { name: userTeam.team2, id: userTeam.team2Id, label: 'Team 2' }
              ].map((team) => {
                const standing = standings?.find((s) => s.team.id.toString() === team.id);
                return (
                  <div key={team.id} className="wc26-user-team-card">
                    {standing?.team.logo && (
                      <img src={standing.team.logo} alt={team.name} />
                    )}
                    <span className="wc26-user-team-label">{team.label}</span>
                    <span className="wc26-user-team-name">{team.name}</span>
                    {findGroup(team.id) && (
                      <span className="wc26-user-team-group">{findGroup(team.id)}</span>
                    )}
                    {(() => {
                      const pts = teamMatchScore(team.id, team.label === 'Team 1');
                      const cls = pts > 0 ? 'pos' : pts < 0 ? 'neg' : 'zero';
                      const tip = teamCardTooltip(team.id, team.name, team.label === 'Team 1');
                      const ptsEl = (
                        <span className={`wc26-user-team-points ${cls}`}>
                          {`${pts > 0 ? '+' : ''}${pts} pts`}
                        </span>
                      );
                      return tip ? (
                        <Tooltip content={tip} direction="up">{ptsEl}</Tooltip>
                      ) : ptsEl;
                    })()}
                  </div>
                );
              })}
            </div>

            <div className="wc26-user-breakdown">
              <h3>Points Breakdown</h3>
              <BreakdownRow label="Match Results" value={score.score} tooltip={matchResultsTooltip} />
              <BreakdownRow label="Group Winners" value={score.groupWinner} tooltip={groupWinnersTooltip} />
              <BreakdownRow label="Golden Boot" value={score.goldenBoot} tooltip={goldenBootTooltip} />
              <BreakdownRow label="Fastest Goal" value={score.earliestGoal} tooltip={fastestGoalTooltip} />
              <BreakdownRow label="Latest Goal" value={score.latestGoal} tooltip={latestGoalTooltip} />
              <BreakdownRow label="Hattrick" value={score.hattrick} tooltip={bonusCountTooltip('Hattrick', score.hattrick, rules?.hattrick ?? 0)} />
              <BreakdownRow label="Direct FK" value={score.directFk} tooltip={bonusCountTooltip('Direct Free Kick', score.directFk, rules?.directFk ?? 0)} />
              <BreakdownRow label="Olimpico" value={score.olimpico} tooltip={bonusCountTooltip('Olimpico', score.olimpico, rules?.olimpico ?? 0)} />
              <BreakdownRow label="Keeper Goal" value={score.keeperGoal} tooltip={bonusCountTooltip('Keeper Goal', score.keeperGoal, rules?.keeperGoal ?? 0)} />
              <BreakdownRow label="Most Reds" value={score.mostReds} tooltip={mostRedsTooltip} />
              <BreakdownRow label="Team 3+ Goals" value={score.teamGoals3plus} tooltip={goals3plusTooltip} />
            </div>

            <div className="wc26-user-matches">
              <h3>Match History</h3>
              {(!matchStats || matchStats.length === 0)
                ? <p className="wc26-user-empty">No matches played yet</p>
                : matchStats.map((match) => (
                  <MatchRow
                    key={match.id}
                    match={match}
                    userTeam={userTeam}
                    rules={rules as any}
                    directFkKeys={directFkGoals ?? []}
                    olimpicoKeys={olimpicoGoals ?? []}
                    keeperKeys={keeperGoals ?? []}
                    mostRedsWinnerIds={mostRedsWinnerIds}
                  />
                ))}
            </div>

            <button
              type="button"
              onClick={() => navigate('/wc26')}
              className="wc26-user-back-btn"
            >
              ← Back to home
            </button>
          </>
        )}
      </div>
      <FooterWC26 />
    </div>
  );
}

export default WC26UserPage;
